import React from 'react';
const { useState, useEffect, useRef } = React;
import { LESSON_KINDS, SUBMISSION_STATES, RESPONSE_TYPES, LEARNING_LIMITS as LL, FILE_FORMATS_TEXT, learningLabel, lessonHasTask, fileTypeOf } from '../learningConfig.js';
import { readFileForUpload, saveDataUrl, fmtSize, VideoPlayer, LessonText, CourseCover } from '../components/learning/learningParts.jsx';

// Mi aprendizaje in the person's space: "Continuar aprendiendo", their course
// cards, and a course with its modules, lessons (the video first), materials
// and practical work, each version with the coach's feedback right under it.
// It only shows what the server sent for this person (shared/learning.ts);
// `call` is the portal's api with the person's session.

const fmtDay = (iso) => (iso ? new Date(iso).toLocaleDateString('es', { day: 'numeric', month: 'short' }) : '');
const fmtWhen = (iso) => (iso ? new Date(iso).toLocaleString('es', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '');
const FILE_ERRORS = { file_type: `Ese formato no se acepta: ${FILE_FORMATS_TEXT}.`, file_too_large: 'El archivo pasa de 4 MB.', unreadable: 'No se pudo leer el archivo.' };
const ERRORS = {
  'invalid_input: missing answer': 'Escribe tu respuesta (o adjunta un archivo) antes de entregar.',
  'invalid_input: missing file': 'Este trabajo pide un archivo adjunto.',
  'invalid_input: waiting for review': 'Ya entregaste esta versión: espera la devolución.',
  'invalid_input: already completed': 'Este trabajo ya está completado.',
  'invalid_input: file type': `Ese formato no se acepta: ${FILE_FORMATS_TEXT}.`,
  'invalid_input: file too large': 'El archivo pasa de 4 MB.',
  'invalid_input: upload failed': 'No se pudo subir el archivo. Vuelve a intentarlo.',
};
const errText = (e) => ERRORS[e.code] || e.message;

/** How a lesson is, in words and with an icon that does not rely on color alone. */
const STATE = {
  not_started: { label: 'Por empezar', icon: '', cls: '' },
  in_progress: { label: 'En curso', icon: '•', cls: 'doing' },
  viewed: { label: 'Vista', icon: '✓', cls: 'done' },
  draft: { label: 'Borrador guardado', icon: '✎', cls: 'doing' },
  submitted: { label: 'Esperando devolución', icon: '…', cls: 'wait' },
  changes_requested: { label: 'Ajustes pedidos', icon: '!', cls: 'fix' },
  completed: { label: 'Completada', icon: '✓', cls: 'done' },
};
const kindLabel = (l) => (l.kind === 'task' ? 'Trabajo práctico' : `${learningLabel(LESSON_KINDS, l.kind)}${lessonHasTask(l) ? ' + trabajo práctico' : ''}`);

/** Lessons completed under the real rules (seen, or a task completed after review): the words say what it counts. */
function Bar({ done, total }) {
  const pct = total ? Math.round((done / total) * 100) : 0;
  const text = `${done} de ${total} ${total === 1 ? 'lección completada' : 'lecciones completadas'}`;
  return (
    <div className="lrn-bar">
      <div className="track" role="progressbar" aria-label="Lecciones completadas" aria-valuemin={0} aria-valuemax={total} aria-valuenow={done} aria-valuetext={text}><div className="fill" style={{ width: `${pct}%` }} /></div>
      <span>{text}</span>
    </div>
  );
}

/** One line about the course, the most useful one only. */
function CourseStatus({ c }) {
  if (c.status === 'completed') return <span className="lrn-pill ok">✓ Terminado</span>;
  if (c.changes) return <span className="lrn-pill warn">Tienes una devolución con ajustes</span>;
  if (c.waiting) return <span className="lrn-pill info">{c.waiting === 1 ? 'Un trabajo espera devolución' : `${c.waiting} trabajos esperan devolución`}</span>;
  if (!c.done && !c.lastActivityAt) return <span className="lrn-pill">Nuevo</span>;
  return <span className="lrn-pill ok">En curso</span>;
}

/** The course to continue: the most recent one with something to do. */
const continueOf = (courses) => [...courses].filter(c => c.status !== 'completed' && c.next)
  .sort((a, b) => String(b.lastActivityAt || '').localeCompare(String(a.lastActivityAt || '')))[0] || null;

/** "Continuar aprendiendo" and the course cards. */
export function LearningList({ courses, onOpen, title = 'Mi aprendizaje', inProcess = false }) {
  if (!courses?.length) return null;
  const cont = continueOf(courses);
  // The course in "Continuar aprendiendo" is not repeated as a card below
  const others = courses.filter(c => c.id !== cont?.id);
  return (
    <section className="lrn-section" aria-label={title}>
      <h2>{title}</h2>
      {cont && (
        <article className="lrn-continue" aria-label="Continuar aprendiendo">
          <CourseCover cover={cont.cover} title={cont.courseName} height={124} />
          <div>
            <div className="lrn-eyebrow">{cont.lastActivityAt ? 'Continuar aprendiendo' : 'Para empezar'}</div>
            <h3>{cont.courseName}</h3>
            <div className="row" style={{ margin: '0 0 8px' }}><CourseStatus c={cont} />{cont.processName && !inProcess && <span className="muted">De tu proceso «{cont.processName}»</span>}</div>
            <p className="lrn-next">
              <span className="muted">Próximo paso · {cont.next.moduleTitle ? `${cont.next.moduleTitle} · ` : ''}{kindLabel(cont.next)}</span><br />
              <b>{cont.next.title}</b>
            </p>
            <div style={{ maxWidth: 360, marginBottom: 12 }}><Bar done={cont.done} total={cont.total} /></div>
            <div className="row">
              <button className="btn primary" onClick={() => onOpen(cont, cont.next.id)}>{cont.lastActivityAt ? 'Continuar' : 'Empezar'}</button>
              <button className="linkish" onClick={() => onOpen(cont, null)}>Ver todo el curso</button>
            </div>
          </div>
        </article>
      )}
      {others.length > 0 && <>
        {cont && <h3 style={{ fontSize: 16, margin: '4px 0 10px' }}>{others.length === 1 ? 'Tu otro curso' : 'Tus otros cursos'}</h3>}
        <div className="lrn-grid">
          {others.map(c => (
            <article key={c.id} className="lrn-card" aria-label={c.courseName}>
              <CourseCover cover={c.cover} title={c.courseName} height={118} rounded={0} />
              <div className="lrn-body">
                <div className="row" style={{ justifyContent: 'space-between' }}><CourseStatus c={c} /><span className="muted">{c.modules} {c.modules === 1 ? 'módulo' : 'módulos'}</span></div>
                <h3>{c.courseName}</h3>
                {c.description && <p className="lrn-desc">{c.description}</p>}
                <Bar done={c.done} total={c.total} />
                {c.processName && !inProcess && <p className="muted" style={{ margin: 0 }}>De tu proceso «{c.processName}»</p>}
                <button className="btn" onClick={() => onOpen(c, null)}>{c.status === 'completed' ? 'Ver curso' : 'Abrir curso'}</button>
              </div>
            </article>
          ))}
        </div>
      </>}
    </section>
  );
}

function Materials({ ids, files, onOpen }) {
  if (!ids?.length) return null;
  return (
    <div className="lrn-block lrn-materials">
      <h3>📎 Materiales</h3>
      {ids.map(id => (
        <div key={id} className="lrn-file">
          <span className="ext">{fileTypeOf(files[id]?.name)?.ext || 'archivo'}</span>
          <span className="name">{files[id]?.name || 'archivo'} <span className="muted">· {files[id] ? fmtSize(files[id].size) : ''}</span></span>
          <button className="btn small" onClick={() => onOpen(id)} aria-label={`Descargar ${files[id]?.name || 'archivo'}`}>Descargar</button>
        </div>
      ))}
    </div>
  );
}

function AttachedFiles({ ids, files, onOpen, onRemove }) {
  if (!ids?.length) return null;
  return ids.map(id => (
    <div key={id} className="lrn-file">
      <span className="ext">{fileTypeOf(files[id]?.name)?.ext || 'archivo'}</span>
      <span className="name">{files[id]?.name || 'archivo'}</span>
      <button className="linkish" onClick={() => onOpen(id)}>Abrir</button>
      {onRemove && <button className="linkish" onClick={() => onRemove(id)}>Quitar</button>}
    </div>
  ));
}

/** The practical work: each handed-in version with its feedback right under it, and the draft. */
function Task({ lesson, view, call, reload, openFile }) {
  const attempts = view.submissions.filter(s => s.lessonId === lesson.id).sort((a, b) => a.attempt - b.attempt);
  const last = attempts[attempts.length - 1] || null;
  const handedIn = attempts.filter(a => a.status !== 'draft');
  const [text, setText] = useState(last?.status === 'draft' ? last.text : '');
  const [fileIds, setFileIds] = useState(last?.status === 'draft' ? last.fileIds : []);
  const [extraFiles, setExtraFiles] = useState({});
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [newVersion, setNewVersion] = useState(false);
  const editorRef = useRef(null);
  const editing = !last || last.status === 'draft' || newVersion;
  const files = { ...view.files, ...extraFiles };
  const allowText = lesson.response !== 'file', allowFile = lesson.response !== 'text';
  const once = async (fn) => { if (busy) return; setBusy(true); setMsg(null); try { await fn(); } catch (e) { setMsg({ err: true, text: errText(e) }); } setBusy(false); };
  const save = () => once(async () => { await call({ action: 'learningDraft', enrollmentId: view.enrollment.id, lessonId: lesson.id, text, fileIds }); setMsg({ text: 'Borrador guardado.' }); await reload(); });
  const submit = () => once(async () => {
    if (!window.confirm('¿Entregar este trabajo? Después no se puede cambiar: si tu coach pide ajustes, presentas una nueva versión.')) return;
    const r = await call({ action: 'learningDraft', enrollmentId: view.enrollment.id, lessonId: lesson.id, text, fileIds });
    await call({ action: 'learningSubmit', submissionId: r.submission.id });
    setNewVersion(false); setMsg({ text: 'Entregado. Verás aquí la devolución de tu coach.' }); await reload();
  });
  const upload = (e) => {
    const file = e.target.files?.[0]; e.target.value = '';
    if (!file) return;
    once(async () => {
      let f;
      try { f = await readFileForUpload(file); } catch (err) { throw Object.assign(new Error(FILE_ERRORS[err.code] || 'No se pudo leer el archivo.'), { code: '' }); }
      const r = await call({ action: 'learningUpload', enrollmentId: view.enrollment.id, lessonId: lesson.id, name: f.name, data: f.data });
      setExtraFiles({ ...extraFiles, [r.file.id]: r.file }); setFileIds([...fileIds, r.file.id]);
    });
  };
  const startNew = () => { setText(last?.text || ''); setFileIds([]); setNewVersion(true); setTimeout(() => editorRef.current?.focus(), 0); };
  const state = STATE[last ? last.status : 'not_started'];
  const nextNumber = handedIn.length + 1;
  return (
    <section className="lrn-block lrn-task" aria-label="Trabajo práctico">
      <h3>✍️ Trabajo práctico {last && <span className={`lrn-pill ${state.cls === 'done' ? 'ok' : state.cls === 'fix' ? 'warn' : state.cls === 'wait' ? 'info' : ''}`}>{state.label}</span>}</h3>
      {lesson.instructions && <LessonText text={lesson.instructions} />}
      <p className="muted" style={{ margin: '0 0 4px' }}>{learningLabel(RESPONSE_TYPES, lesson.response)}{allowFile ? ` · ${FILE_FORMATS_TEXT}` : ''}.</p>

      {handedIn.map(a => (
        <div key={a.id} className="lrn-version">
          <div className="meta"><b>Tu entrega {a.attempt}</b><span className="muted">· {fmtWhen(a.submittedAt)}</span></div>
          {a.text && <p className="lrn-answer">{a.text}</p>}
          <AttachedFiles ids={a.fileIds} files={files} onOpen={openFile} />
          {a.feedback
            ? <div className={`lrn-feedback ${a.status === 'changes_requested' ? 'fix' : ''}`}><div className="who">Devolución de tu coach · {a.status === 'changes_requested' ? 'pide ajustes' : 'completado'} · {fmtDay(a.reviewedAt)}</div><LessonText text={a.feedback} /></div>
            : a.status === 'submitted' && <p className="muted" style={{ margin: '8px 0 0' }}>Tu coach lo revisará y verás aquí su devolución.</p>}
        </div>
      ))}

      {last?.status === 'changes_requested' && !newVersion && <div style={{ marginTop: 12 }}><button className="btn primary" onClick={startNew}>Presentar la versión {nextNumber}</button></div>}

      {editing && (
        <div className="lrn-version" style={{ borderStyle: 'dashed' }}>
          <div className="meta" style={{ justifyContent: 'space-between' }}><b>{handedIn.length ? `Versión ${nextNumber}` : 'Tu respuesta'}</b><span className="vis private">👁 Solo tú, hasta entregarla</span></div>
          {allowText && <textarea ref={editorRef} value={text} maxLength={LL.answer} onChange={e => setText(e.target.value)} aria-label="Tu respuesta" style={{ marginTop: 8 }} placeholder="Escribe aquí tu respuesta…" />}
          {allowFile && <div style={{ marginTop: 8 }}>
            <AttachedFiles ids={fileIds} files={files} onOpen={openFile} onRemove={(id) => setFileIds(fileIds.filter(x => x !== id))} />
            {fileIds.length < LL.filesPerSubmission && <label className="btn small" style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', marginTop: 6 }}>Adjuntar archivo<input type="file" onChange={upload} disabled={busy} style={{ position: 'absolute', opacity: 0, width: 1, height: 1 }} /></label>}
          </div>}
          <div className="row" style={{ marginTop: 10 }}>
            <button className="btn primary" disabled={busy || (!text.trim() && !fileIds.length)} onClick={submit}>Entregar</button>
            <button className="btn" disabled={busy} onClick={save}>Guardar borrador</button>
          </div>
        </div>
      )}
      {msg && <div className={`msg ${msg.err ? 'err' : 'ok'}`} role="status" style={{ marginTop: 10, marginBottom: 0 }}>{msg.text}</div>}
    </section>
  );
}

function Lesson({ lesson, position, state, view, call, reload, openFile }) {
  const [busy, setBusy] = useState(false);
  useEffect(() => { call({ action: 'learningOpen', enrollmentId: view.enrollment.id, lessonId: lesson.id }).then(() => reload(true)).catch(() => {}); }, [lesson.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const toggle = async (viewed) => { if (busy) return; setBusy(true); try { await call({ action: 'learningViewed', enrollmentId: view.enrollment.id, lessonId: lesson.id, viewed }); await reload(); } catch { /* the list shows the last state */ } setBusy(false); };
  const hasTask = lessonHasTask(lesson);
  const seen = view.enrollment.progress?.[lesson.id]?.state === 'viewed';
  return (
    <article className="lrn-lesson" aria-labelledby="lrn-lesson-title">
      <div className="lrn-eyebrow">{position}</div>
      <h2 id="lrn-lesson-title">{lesson.title}</h2>
      {lesson.kind === 'video' && <div className="lrn-video-wrap"><VideoPlayer url={lesson.videoUrl} title={lesson.title} /></div>}
      {lesson.kind !== 'task' && <div className="lrn-text"><LessonText text={lesson.body} /></div>}
      <Materials ids={lesson.fileIds} files={view.files} onOpen={openFile} />
      {hasTask && <Task key={lesson.id} lesson={lesson} view={view} call={call} reload={reload} openFile={openFile} />}
      {lesson.kind !== 'task' && (
        <div className="lrn-done-row">
          {seen
            ? <><span className="lrn-pill ok">✓ La marcaste como vista</span><button className="linkish" disabled={busy} onClick={() => toggle(false)}>Desmarcar</button></>
            : <button className="btn" disabled={busy} onClick={() => toggle(true)}>Marcar como vista</button>}
          <span className="muted">{hasTask ? 'La lección se completa cuando tu coach revisa el trabajo.' : lesson.kind === 'video' ? 'Reproducir el video no la marca: lo decides tú.' : ''}</span>
        </div>
      )}
    </article>
  );
}

/** One assigned course: header, the contents (modules and lessons), the open lesson and the way to the next one. */
export function LearningCourse({ enrollmentId, startAt = null, call, onBack, onExpired }) {
  const [view, setView] = useState(null);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(null);
  const [note, setNote] = useState('');
  const topRef = useRef(null);
  const reload = (quiet = false) => call({ action: 'learningCourse', enrollmentId }).then(v => { setView(v); return v; }).catch(e => { if (e.code === 'unauthorized') onExpired?.(); if (!quiet) setError(e.message); });
  useEffect(() => { reload().then(v => { if (v) setOpen(startAt || v.enrollment.lastLessonId || v.progress.next?.id || v.progress.states[0]?.id || null); }); }, [enrollmentId]); // eslint-disable-line react-hooks/exhaustive-deps
  const go = (id) => { setOpen(id); setNote(''); topRef.current?.scrollIntoView({ block: 'start' }); };
  const openFile = async (fileId) => {
    try {
      const r = await call({ action: 'learningFile', enrollmentId, fileId });
      setNote(saveDataUrl(r.dataUrl, r.file.name) ? '' : `En la demo no se descargan archivos: «${r.file.name}» (${fmtSize(r.file.size)}) llegó por Oike con tu permiso.`);
    } catch (e) { setNote(e.message); }
  };
  if (error) return <div className="card msg err">{error}</div>;
  if (!view) return <div className="card">Cargando…</div>;
  const lessons = view.snapshot.modules.flatMap((m, mi) => m.lessons.map((l, li) => ({ ...l, moduleTitle: m.title, mi, li, of: m.lessons.length })));
  const idx = lessons.findIndex(l => l.id === open);
  const lesson = lessons[idx];
  const stateOf = (id) => view.progress.states.find(s => s.id === id);
  const prev = lessons[idx - 1], next = lessons[idx + 1];
  return (
    <div ref={topRef}>
      {onBack && <div className="row" style={{ margin: '2px 0 10px' }}><button className="btn small" onClick={onBack}>← Mi aprendizaje</button></div>}
      <header className="lrn-head">
        <CourseCover cover={view.snapshot.cover} title={view.course.name} height={84} showExample={false} />
        <div>
          <h1>{view.course.name}</h1>
          <Bar done={view.progress.done} total={view.progress.total} />
        </div>
      </header>

      <details className="lrn-toc">
        <summary>Contenido del curso <span className="muted" style={{ fontWeight: 500 }}>{view.snapshot.modules.length} módulos · {lessons.length} lecciones</span></summary>
        {view.snapshot.modules.map((m, mi) => (
          <div key={m.id} className="lrn-module">
            <h4>{m.title}</h4>
            {m.lessons.map(l => {
              const st = STATE[stateOf(l.id)?.state || 'not_started'];
              return (
                <button key={l.id} className="lrn-lesson-link" aria-current={l.id === open ? 'true' : undefined} onClick={() => go(l.id)}>
                  <span className={`lrn-icon ${st.cls}`} aria-hidden="true">{st.icon}</span>
                  <span className="t">{l.title}<br /><span className="muted">{kindLabel(l)}</span></span>
                  <span className="muted" style={{ fontSize: 12 }}>{st.label}</span>
                </button>
              );
            })}
          </div>
        ))}
      </details>

      {note && <div className="msg ok" role="status">{note}</div>}
      {lesson && <Lesson key={lesson.id} lesson={lesson} position={`${lesson.moduleTitle} · Lección ${lesson.li + 1} de ${lesson.of}`} state={stateOf(lesson.id)} view={view} call={call} reload={reload} openFile={openFile} />}
      {lesson && (
        <nav className="lrn-nav" aria-label="Lecciones">
          <button className="btn" disabled={!prev} onClick={() => go(prev.id)}>← Anterior{prev && <small>{prev.title}</small>}</button>
          <button className="btn" disabled={!next} onClick={() => go(next.id)}>Siguiente →{next && <small>{next.title}</small>}</button>
        </nav>
      )}

      {view.removed.length > 0 && (
        <details className="lrn-toc">
          <summary>Entregas de lecciones anteriores</summary>
          <div className="lrn-module">
            <p className="muted">Son de lecciones que ya no están en tu versión del curso. Se conservan con sus devoluciones.</p>
            {view.submissions.filter(s => view.removed.includes(s.lessonId)).map(s => (
              <div key={s.id} className="lrn-version"><div className="meta"><b>Entrega {s.attempt}</b><span className="muted">· {learningLabel(SUBMISSION_STATES, s.status)}</span></div>{s.text && <p className="lrn-answer">{s.text}</p>}{s.feedback && <div className="lrn-feedback"><div className="who">Devolución</div><LessonText text={s.feedback} /></div>}</div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}
